import { z } from 'zod';
import { DateField, DateTimeField } from '@mews-commander/form';
import {
    DateTimeUtcService,
    DateTimeUtils,
    DurationService,
    TimeUnitUtils,
    dateTimeUtcStringSchema,
    lt,
    useTimezone,
} from '@mews/framework';
import { AvailabilityBlock, ReleaseStrategy } from '@mews-commander/data-access';
import { TimeUnit } from '@mews/enums';
import { Stack } from '@optimus-web/core';
import { getNowUtc, useCommanderSelector } from '@mews-commander/utils';
import { getServiceTimeUnit } from '@mews-commander/legacy-entities/src/ducks';
import { getCurrentEnterpriseMinimalOperationEditableUtc } from '../../selectors';

enum FormFields {
    firstTimeUnitStartUtc = 'firstTimeUnitStartUtc',
    lastTimeUnitStartUtc = 'lastTimeUnitStartUtc',
}
export const formSchema = z.object({
    [FormFields.firstTimeUnitStartUtc]: dateTimeUtcStringSchema,
    [FormFields.lastTimeUnitStartUtc]: dateTimeUtcStringSchema,
});
export type FormValues = z.infer<typeof formSchema>;

const commonFirstTimeUnitProps = {
    name: FormFields.firstTimeUnitStartUtc,
    label: lt('StartOn'),
    required: true,
    size: 'medium' as const,
};
const commonLastTimeUnitProps = {
    name: FormFields.lastTimeUnitStartUtc,
    label: lt('EndOn'),
    required: true,
    size: 'medium' as const,
};

type Props = Pick<AvailabilityBlock, 'startUtc' | 'endUtc' | 'releaseStrategy' | 'serviceId'>;

const getMaxRollingReleaseDateUtc = (durationString: string) => {
    const secondDuration = DurationService.asSeconds(DurationService.parse(durationString));
    const nowUtc = DateTimeUtcService.parse(getNowUtc());
    return DateTimeUtcService.addSeconds(nowUtc, secondDuration).value;
};

export const OverrideAvailabilityAdjustmentFields = ({ startUtc, endUtc, releaseStrategy, serviceId }: Props) => {
    const timezone = useTimezone();
    const timeUnit = useCommanderSelector((s) => getServiceTimeUnit(s, { id: serviceId }));

    const minEditableUtc = useCommanderSelector(getCurrentEnterpriseMinimalOperationEditableUtc);

    const adjustedMinEditableUtc =
        timeUnit &&
        TimeUnitUtils.getNextTimeUnitStartUtc(minEditableUtc, timezone, TimeUnitUtils.timeUnitToDateTimePart(timeUnit));

    const minUtc = adjustedMinEditableUtc && startUtc < adjustedMinEditableUtc ? adjustedMinEditableUtc : startUtc;
    const minDate = DateTimeUtils.utcToStructuredDate(minUtc, timezone);
    const maxDate = DateTimeUtils.utcToStructuredDate(
        releaseStrategy.discriminator === ReleaseStrategy.RollingReleaseOffset
            ? getMaxRollingReleaseDateUtc(releaseStrategy.value)
            : endUtc,
        timezone
    );

    if (timeUnit === TimeUnit.Hour) {
        return (
            <Stack vertical spacing="200" fill>
                <DateTimeField {...commonFirstTimeUnitProps} dateProps={{ minDate, maxDate }} />
                <DateTimeField {...commonLastTimeUnitProps} dateProps={{ minDate, maxDate }} />
            </Stack>
        );
    }
    return (
        <>
            <DateField {...commonFirstTimeUnitProps} mode="utc" minDate={minDate} maxDate={maxDate} />
            <DateField {...commonLastTimeUnitProps} mode="utc" minDate={minDate} maxDate={maxDate} />
        </>
    );
};
